import axios from 'axios';
import { supabase } from '../lib/supabase';

// Get API URL and ensure it ends with /api
const getApiUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL || '/api';

  // If it already ends with /api, use as-is
  if (envUrl.endsWith('/api')) {
    return envUrl;
  }

  // If it's just '/api', use as-is (default fallback)
  if (envUrl === '/api') {
    return envUrl;
  }

  // Otherwise, append /api to the URL
  return `${envUrl.replace(/\/$/, '')}/api`;
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Session cache to avoid repeated fetches
let sessionCache = {
  session: null,
  timestamp: 0,
  CACHE_DURATION: 30000, // 30 seconds
};

// Helper: Retry with exponential backoff
async function retryWithBackoff(fn, maxRetries = 3, baseDelay = 1000) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === maxRetries - 1) throw error;
      const delay = baseDelay * Math.pow(2, i);
      console.warn(`[API] Retry ${i + 1}/${maxRetries} after ${delay}ms:`, error.message);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

// Helper: Get session with cache
async function getSessionWithTimeout() {
  // Check cache first
  const now = Date.now();
  if (sessionCache.session && (now - sessionCache.timestamp) < sessionCache.CACHE_DURATION) {
    return sessionCache.session;
  }

  // Read session directly from localStorage (synchronous, no race condition)
  try {
    const storageKey = 'pharmunity-auth-token';
    const storedSession = localStorage.getItem(storageKey);

    if (storedSession) {
      const session = JSON.parse(storedSession);
      // Update cache
      sessionCache.session = session;
      sessionCache.timestamp = Date.now();
      return session;
    }

    // Fallback: fetch from Supabase if not in localStorage
    const { data: { session } } = await supabase.auth.getSession();

    // Update cache
    sessionCache.session = session;
    sessionCache.timestamp = Date.now();

    return session;
  } catch (err) {
    console.warn('[API] Session fetch failed:', err.message);
    // Don't clear session on error - just return null
    return null;
  }
}

// Request interceptor - add Supabase auth token
api.interceptors.request.use(
  async (config) => {
    try {
      const session = await getSessionWithTimeout();
      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }
    } catch (err) {
      console.warn('[API] Failed to get session after retries:', err.message);
      // Continue without auth token - the request might still work or return 401
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors with token refresh and retry
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors with retry logic
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.warn('[API] 401 Unauthorized:', error.config?.url);

      originalRequest._retry = true;

      try {
        // Force refresh the session (bypass cache)
        sessionCache.session = null;

        // Try to refresh the session
        const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError || !session) {
          console.error('[API] Session refresh failed:', refreshError?.message);
          // Clear cache and let auth system handle sign-out
          sessionCache.session = null;
          return Promise.reject(error);
        }

        // Update cache with new session
        sessionCache.session = session;
        sessionCache.timestamp = Date.now();

        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
        return api(originalRequest);
      } catch (refreshErr) {
        console.error('[API] Failed to retry after 401:', refreshErr.message);
        sessionCache.session = null;
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// API methods
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

export const sellersAPI = {
  getAll: (params) => api.get('/sellers', { params }),
  getById: (id) => api.get(`/sellers/${id}`),
  create: (data) => api.post('/sellers', data),
  update: (id, data) => api.put(`/sellers/${id}`, data),
  getAssets: (id) => api.get(`/sellers/${id}/assets`),
  addAsset: (id, data) => api.post(`/sellers/${id}/assets`, data),
  updateAsset: (sellerId, assetId, data) =>
    api.put(`/sellers/${sellerId}/assets/${assetId}`, data),
  deleteAsset: (sellerId, assetId) =>
    api.delete(`/sellers/${sellerId}/assets/${assetId}`),
  getMarketPulse: (id) => api.get(`/sellers/${id}/market-pulse`),
};

export const buyersAPI = {
  // Self-service profile endpoints
  getMyProfile: () => api.get('/buyers/me/profile'),
  updateMyProfile: (data) => api.put('/buyers/me/profile', data),
  // Public/Seller endpoints
  getAll: (params) => api.get('/buyers', { params }),
  getById: (id) => api.get(`/buyers/${id}`),
  search: (params) => api.get('/buyers/search', { params }),
  getContacts: (id) => api.get(`/buyers/${id}/contacts`),
  // Intelligence endpoints
  getIntelligence: (buyerId) => api.get(`/buyers/${buyerId}/intelligence`),
  getIntelligenceDetail: (buyerId) => api.get(`/buyers/${buyerId}/intelligence/detail`),
  updateIntelligence: (buyerId, data) => api.put(`/buyers/${buyerId}/intelligence`, data),
  getSignals: (buyerId, params) => api.get(`/buyers/${buyerId}/signals`, { params }),
  createSignal: (buyerId, data) => api.post(`/buyers/${buyerId}/signals`, data),
  updateSignal: (buyerId, signalId, data) => api.put(`/buyers/${buyerId}/signals/${signalId}`, data),
  deleteSignal: (buyerId, signalId) => api.delete(`/buyers/${buyerId}/signals/${signalId}`),
  getActiveBuyers: (params) => api.get('/buyers/intelligence/active', { params }),
  getRecentSignals: (params) => api.get('/buyers/intelligence/signals', { params }),
};

export const matchingAPI = {
  calculate: () => api.post('/matching/calculate'),
  getRecommendations: (sellerId) => api.get(`/matching/recommendations/${sellerId}`),
  getSellerMatches: (sellerId) => api.get(`/matching/recommendations/${sellerId}`),
  getScore: (sellerId, buyerId) => api.get(`/matching/score/${sellerId}/${buyerId}`),
  getBuyerRecommendations: () => api.get('/matching/buyer-recommendations'),
  // New comprehensive intelligence endpoints
  getIntelligence: (buyerId) => api.get(`/matching/intelligence/${buyerId}`),
  getIntelligenceByType: (buyerId, scoreType) => api.get(`/matching/intelligence/${buyerId}/${scoreType}`),
};

export const trackingAPI = {
  recordView: (data) => api.post('/tracking/view', data),
  getBuyerInterest: (sellerId) => api.get(`/tracking/interest/${sellerId}`),
  getRecentActivity: (sellerId, days = 30) =>
    api.get(`/tracking/activity/${sellerId}`, { params: { days } }),
  getViewStats: (sellerId) => api.get(`/tracking/stats/${sellerId}`),
  getHotLeads: (sellerId) => api.get(`/tracking/hot-leads/${sellerId}`),
};

export const meetingsAPI = {
  createRequest: (data) => api.post('/meetings/request', data),
  getIncoming: (sellerId, status) =>
    api.get(`/meetings/incoming/${sellerId}`, { params: { status } }),
  getById: (id) => api.get(`/meetings/${id}`),
  respond: (id, status, message) =>
    api.put(`/meetings/${id}/respond`, { status, response_message: message }),
  getStats: (sellerId) => api.get(`/meetings/seller/${sellerId}/stats`),
};

export const alertsAPI = {
  // Get alerts with filters
  getAlerts: (params) => api.get('/alerts', { params }),
  // Get unread count
  getUnreadCount: () => api.get('/alerts/unread-count'),
  // Mark single alert as read
  markAsRead: (id) => api.put(`/alerts/${id}/read`),
  // Mark all alerts as read
  markAllAsRead: () => api.put('/alerts/read-all'),
  // Dismiss an alert
  dismiss: (id) => api.put(`/alerts/${id}/dismiss`),
  // Get preferences
  getPreferences: () => api.get('/alerts/preferences'),
  // Update preferences
  updatePreferences: (data) => api.put('/alerts/preferences', data),
  // Get alert statistics
  getStats: () => api.get('/alerts/stats'),
};

export const aiAPI = {
  // Generate company description using AI
  generateDescription: (companyData) => api.post('/ai/generate-description', companyData),
};

export const followsAPI = {
  // Follow a seller
  follow: (sellerId) => api.post(`/follows/sellers/${sellerId}`),
  // Unfollow a seller
  unfollow: (sellerId) => api.delete(`/follows/sellers/${sellerId}`),
  // Get followed sellers
  getFollowed: () => api.get('/follows/my'),
  // Check follow status
  checkStatus: (sellerId) => api.get(`/follows/sellers/${sellerId}/status`),
  // Update follow preferences
  updatePreferences: (sellerId, data) => api.put(`/follows/sellers/${sellerId}/preferences`, data),
  // Get follower count
  getFollowerCount: (sellerId) => api.get(`/follows/sellers/${sellerId}/count`),
};

export const postsAPI = {
  // Get posts (public or with auth for buyers_only)
  getAll: (params) => api.get('/posts', { params }),
  // Get seller's posts
  getBySellerForPublic: (sellerId, params) => api.get(`/posts/seller/${sellerId}`, { params }),
  // Get buyer feed (posts from followed sellers)
  getBuyerFeed: (params) => api.get('/posts/feed', { params }),
  // Get post types
  getTypes: () => api.get('/posts/types'),
  // Create a new post (seller only)
  create: (data) => api.post('/posts', data),
  // Update a post (owner only)
  update: (id, data) => api.put(`/posts/${id}`, data),
  // Delete a post (owner only)
  delete: (id) => api.delete(`/posts/${id}`),
  // Pin/unpin a post (owner only)
  togglePin: (id) => api.post(`/posts/${id}/pin`),
  // Get post analytics (owner only)
  getAnalytics: (sellerId) => api.get(`/posts/analytics/${sellerId}`),
  // Track a post view
  trackView: (id) => api.post(`/posts/${id}/view`),
};

export const settingsAPI = {
  // Get user settings
  get: () => api.get('/settings'),
  // Update all settings
  update: (data) => api.put('/settings', data),
  // Update only notification settings
  updateNotifications: (data) => api.put('/settings/notifications', data),
  // Update only privacy settings
  updatePrivacy: (data) => api.put('/settings/privacy', data),
};

export const messagesAPI = {
  // Search for message recipients
  searchRecipients: (query, type) => api.get('/messages/search-recipients', { params: { q: query, type } }),
  // Send email notification for a new message
  notifyNewMessage: (data) => api.post('/messages/notify', data),
};

export const accessAPI = {
  // Buyer: Request access to a seller's deeper information
  requestAccess: (sellerId, data) => api.post(`/access/request/${sellerId}`, data),
  // Buyer: Get my access requests
  getMyRequests: () => api.get('/access/my-requests'),
  // Buyer: Get my access grants
  getMyGrants: () => api.get('/access/my-grants'),
  // Get access level for a specific seller
  getAccessLevel: (sellerId) => api.get(`/access/level/${sellerId}`),
  // Buyer: Get recently approved access requests (for notifications)
  getRecentApprovals: () => api.get('/access/recent-approvals'),
  // Buyer: Acknowledge an approved access request (dismiss notification)
  acknowledgeApproval: (requestId) => api.put(`/access/requests/${requestId}/acknowledge`),
  // Seller: Get incoming access requests
  getIncomingRequests: (status) => api.get('/access/incoming', { params: { status } }),
  // Seller: Approve an access request
  approveRequest: (requestId, data) => api.put(`/access/requests/${requestId}/approve`, data),
  // Seller: Reject an access request
  rejectRequest: (requestId, data) => api.put(`/access/requests/${requestId}/reject`, data),
  // Seller: Get buyers who have been granted access
  getGrantedBuyers: () => api.get('/access/granted-buyers'),
  // Seller: Revoke access from a buyer
  revokeAccess: (buyerId) => api.delete(`/access/grants/${buyerId}`),
};

export const companiesAPI = {
  // Check if company exists by email domain
  checkDomain: (email) => api.get('/companies/check-domain', { params: { email } }),
  // Get company by ID
  getById: (id) => api.get(`/companies/${id}`),
  // Get current user's company
  getMyCompany: () => api.get('/companies/my/company'),
  // Create or join a company
  createOrJoin: (data) => api.post('/companies', data),
  // Update company profile
  update: (id, data) => api.put(`/companies/${id}`, data),
  // Get company edit history
  getEditHistory: (id, params) => api.get(`/companies/${id}/history`, { params }),
  // Get sub-organizations for a company
  getSubOrganizations: (id) => api.get(`/companies/${id}/sub-orgs`),
  // Create a sub-organization
  createSubOrganization: (id, data) => api.post(`/companies/${id}/sub-orgs`, data),
  // Get colleague activity
  getColleagueActivity: (id, params) => api.get(`/companies/${id}/colleague-activity`, { params }),
  // Get colleagues who have interacted with a specific seller
  getColleaguesForSeller: (sellerId) => api.get(`/companies/colleagues-for-seller/${sellerId}`),
  // Get user personal profile
  getUserProfile: () => api.get('/companies/profile'),
  // Update user personal profile
  updateUserProfile: (data) => api.put('/companies/profile', data),
};

export const dealIntelligenceAPI = {
  // Get comprehensive deal intelligence for a seller
  getIntelligence: (sellerId) => api.get(`/deals/intelligence/${sellerId}`),
  // Get personalized valuation range
  getValuation: (sellerId) => api.get(`/deals/intelligence/${sellerId}/valuation`),
  // Get comparable deals with similarity scores
  getComparables: (sellerId, params) => api.get(`/deals/intelligence/${sellerId}/comparables`, { params }),
  // Get actionable market insights
  getInsights: (sellerId) => api.get(`/deals/intelligence/${sellerId}/insights`),
  // Get recommended buyers based on deal patterns
  getBuyers: (sellerId) => api.get(`/deals/intelligence/${sellerId}/buyers`),
  // Force refresh intelligence calculations
  refresh: (sellerId) => api.post(`/deals/intelligence/${sellerId}/refresh`),
};

export const catalystsAPI = {
  // Get upcoming catalyst events
  getUpcoming: (params) => api.get('/catalysts', { params }),
  // Get catalyst statistics
  getStats: () => api.get('/catalysts/stats'),
  // Get catalyst calendar view
  getCalendar: (params) => api.get('/catalysts/calendar', { params }),
  // Get catalysts relevant to a seller
  getForSeller: (sellerId) => api.get(`/catalysts/seller/${sellerId}`),
  // Get competitor catalysts
  getCompetitors: (sellerId) => api.get(`/catalysts/competitors/${sellerId}`),
  // Import catalysts (admin only)
  import: (events) => api.post('/catalysts/import', { events }),
};

export default api;
